import { createContentPath, createScopedId } from '@kcftech/prometheus-files-sdk';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { useCallback } from 'react';
import { queryClient } from 'utils/query';

import { toRequiredScope } from '../path';

export function after(path: string, pattern: string) {
    const index = path.lastIndexOf(pattern);
    return index !== -1 ? path.slice(index + pattern.length) : '';
}

function trimEnd(str: string, char: string) {
    let endIdx = str.length - 1;
    while (str[endIdx] === char) {
        endIdx -= 1;
    }
    return str.substring(0, endIdx + 1);
}

type Path = string;
type Id = string;
export type PathToIdMapping = { path: Path; id: Id; name: string };

export type UseFileIdsParams = {
    locationId: string | undefined;
    corporationId: string | undefined;
    /** `/abc/123/465/` */
    path: string | undefined;
    id: string | undefined;
};

const FOLDER_IDS_STALE_TIME = 10 * 60 * 1000; // 10 minutes

function buildFolderIdsQueryKey({ path, id }: Pick<UseFileIdsParams, 'path' | 'id'>) {
    return ['folder-ids-in-path', id ?? path] as const;
}

async function fetchFolderIdsInPath({
    locationId,
    corporationId,
    path,
    id,
}: UseFileIdsParams): Promise<PathToIdMapping[]> {
    if (!path && !id) {
        throw new Error('at least one of path or id is required');
    }

    const scope = toRequiredScope(corporationId, locationId);
    const result = await sdClient.resolveFoldersInPath({
        path: id
            ? createScopedId({ ...scope, fileId: id })
            : createContentPath({
                  ...scope,
                  filePath: path ?? '/',
              }),
    });

    const out = new Array(result.length);
    for (let i = 0; i < result.length; i++) {
        out[i] = {
            path: result[i].Path,
            id: result[i].Id,
            name: after(trimEnd(result[i].Path, '/'), '/'),
        };
    }
    return out;
}

export function useFolderIdsInPath(params: UseFileIdsParams) {
    return useQuery<PathToIdMapping[]>({
        queryKey: buildFolderIdsQueryKey(params),
        queryFn: () => fetchFolderIdsInPath(params),
        placeholderData: keepPreviousData,
        staleTime: FOLDER_IDS_STALE_TIME,
        refetchOnMount: false,
    });
}

export function usePrefetchFolderIdsInPath() {
    return useCallback(
        (params: UseFileIdsParams) =>
            queryClient.prefetchQuery({
                queryKey: buildFolderIdsQueryKey(params),
                queryFn: () => fetchFolderIdsInPath(params),
                staleTime: FOLDER_IDS_STALE_TIME,
            }),
        [],
    );
}
